// artifacts/edison/src/components/tour/tour-provider.tsx
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { TOUR_STEPS } from './tour-steps.js';

const LS_TOUR_DONE = 'edison:tour-completed';

interface TourContextValue {
  active: boolean;
  stepIndex: number;
  totalSteps: number;
  startTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside TourProvider');
  return ctx;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const stepIndexRef = useRef(0);

  // Keep the ref in sync with state for synchronous reads in callbacks
  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  // Auto-start on first ever visit (1s delay so the page renders first)
  useEffect(() => {
    if (localStorage.getItem(LS_TOUR_DONE)) return;
    const t = setTimeout(() => setActive(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setActive(true);
  }, []);

  const endTour = useCallback(() => {
    setActive(false);
    localStorage.setItem(LS_TOUR_DONE, 'true');
  }, []);

  const nextStep = useCallback(() => {
    const current = stepIndexRef.current;
    if (current >= TOUR_STEPS.length - 1) {
      setActive(false);
      setStepIndex(0);
      localStorage.setItem(LS_TOUR_DONE, 'true');
    } else {
      setStepIndex(current + 1);
    }
  }, []);

  const prevStep = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  return (
    <TourContext.Provider
      value={{
        active,
        stepIndex,
        totalSteps: TOUR_STEPS.length,
        startTour,
        endTour,
        nextStep,
        prevStep,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}
